import EyeFilled from '@ant-design/icons/lib/icons/EyeFilled'
import EyeInvisibleFilled from '@ant-design/icons/lib/icons/EyeInvisibleFilled'
import Icon from '@ant-design/icons/lib/components/Icon'
import Button from 'antd/lib/button'
import Flex from 'antd/lib/flex'
import Search from 'antd/lib/input/Search'
import Tooltip from 'antd/lib/tooltip'
import { ClassesPropsType } from 'Components/tasks/Classes/Classes'
import { FocusIcon } from 'Components/common/Icons/FocusIcon'
import { UnFocusIcon } from 'Components/common/Icons/UnfocusedIcon'
import { isEmpty } from 'lodash'
import { useCallback } from 'react'
import { clsx } from 'shared/utils/react.utils'
import classes from './Classes.module.less'

type ClassesListTabProps = ClassesPropsType & {
    getFilteredClasses: () => ClassesPropsType['classes']
    getSearchText: () => string
    setSearchText: (value: string) => void
}

const VisibleEyeComponent = (props: {
    id: string
    isVisible: boolean
    annotation: ReturnType<ClassesPropsType['getAnnotationList']>[number] | undefined
    setClassVisibility: (id: string, isVisible: boolean) => void
}) => {
    if (isEmpty(props.annotation?.selection.individualRowSelectionRange)) {
        return <EyeInvisibleFilled className={classes['empty-icon']} />
    }

    return (
        <Button
            onClick={event => {
                event.stopPropagation()
                props.setClassVisibility(props.id, !props.isVisible)
            }}
            data-testid={`toggle-visibility-${props.id}`}
        >
            {props.isVisible ? (
                <EyeFilled className={classes['visible-icon']} />
            ) : (
                <EyeInvisibleFilled className={classes['hidden-icon']} />
            )}
        </Button>
    )
}

const FocusComponent = (props: {
    id: string
    focusedClass: string | null
    onClickFocus: (id: string) => void
}) => (
    <Button
        onClick={event => {
            event.stopPropagation()
            props.onClickFocus(props.id)
        }}
        data-testid={`toggle-focus-${props.id}`}
    >
        {props.focusedClass === props.id ? (
            <Icon className={classes['class-focus-selected']} component={FocusIcon} />
        ) : (
            <Icon className={classes['class-focus-default']} component={UnFocusIcon} />
        )}
    </Button>
)

export const ClassesListTab = (props: ClassesListTabProps) => {
    const focusedClass = props.getFocusedClass()
    const searchText = props.getSearchText()
    const filteredClasses = props.getFilteredClasses()
    const isAllClassesHidden = props.classes.every(classItem => !classItem.isVisible)

    const onClickFocus = useCallback(
        (id: string) => {
            const nextFocus = focusedClass === id ? null : id
            props.setClassFocus(id, nextFocus !== null)
        },
        [focusedClass, props]
    )

    const onClickVisibleIcon = useCallback(
        (id: string, value: boolean) => {
            if (focusedClass) {
                props.clearFocusedClass()
            }
            props.setClassVisibility(id, value)
        },
        [focusedClass, props]
    )

    return (
        <>
            <Flex gap={4} className={classes['class-controls']}>
                <Search
                    placeholder="Search classes..."
                    value={searchText}
                    onChange={event => props.setSearchText(event.target.value)}
                    className={classes.search}
                />

                <Tooltip title="Show/Hide all annotations" placement="bottomLeft">
                    <Button
                        className={classes['toggle-visibility-button']}
                        onClick={() => onClickVisibleIcon('all', isAllClassesHidden)}
                        data-testid="toggle-visibility"
                    >
                        {isAllClassesHidden ? <EyeInvisibleFilled /> : <EyeFilled />}
                    </Button>
                </Tooltip>
            </Flex>

            {filteredClasses.map(classItem => (
                <Flex
                    className={clsx(
                        classes['class-item'],
                        props.getActiveClass() === classItem.id ? classes['active'] : undefined
                    )}
                    justify="space-between"
                    align="center"
                    gap={8}
                    onClick={() => {
                        if (focusedClass) {
                            props.setClassFocus(focusedClass, false)
                        }
                        props.setActiveClass(classItem.id)
                    }}
                    key={classItem.id}
                >
                    <div
                        className={classes['class-item-color']}
                        style={{ backgroundColor: classItem.color }}
                    />
                    <div className={classes['class-item-name']}>{classItem.label}</div>
                    <div className={classes['class-item-action']}>
                        <FocusComponent
                            id={classItem.id}
                            focusedClass={focusedClass}
                            onClickFocus={onClickFocus}
                        />
                        <VisibleEyeComponent
                            isVisible={classItem.isVisible}
                            annotation={props
                                .getAnnotationList()
                                .find(annotation => annotation.classId === classItem.id)}
                            id={classItem.id}
                            setClassVisibility={onClickVisibleIcon}
                        />
                    </div>
                </Flex>
            ))}
        </>
    )
}
